/**
 * «Последние заказы» — a teaser, not the History tab.
 *
 * Three rows, because the tab next door is where the rest lives. Its job is the one-tap repeat:
 * for somebody who fuels the same car at the same address every week, the whole app is this block.
 *
 * **«Повторить» sits on the first row only, and on the row it repeats.** iOS learned this the hard
 * way — the link used to live beside the section heading, where «Последние заказы … Повторить»
 * gave no clue *what* it would repeat. It is a sibling of the row's link, never nested inside it:
 * a control inside a link is a hit-target nobody can predict.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  fetchOrderHistory,
  type ConsumerOrderHistoryRow,
} from '@/entities/order/api';
import {
  historyCarLabel,
  historyDateTimeLabel,
  historyRow,
} from '@/entities/order/model/history';
import { CarBrandLogo } from '@/shared/ui/CarBrandLogo';
import { RepeatOrderLink } from './RepeatOrderLink';

/** Small enough to read as a teaser. The History tab carries the rest. */
const PREVIEW_SIZE = 3;

export function RecentOrders() {
  const recents = useQuery({
    queryKey: ['order-history', 'recents'],
    queryFn: () => fetchOrderHistory(PREVIEW_SIZE, 0),
    // Decoration, like the unread badge: a failure here must never become a state of the screen.
    retry: false,
  });

  const orders = recents.data?.orders ?? [];
  if (orders.length === 0) return null;

  return (
    <section className="flex flex-col gap-1.5">
      <h2 className="px-0.5 pt-1 text-[12px] font-bold uppercase tracking-[0.05em] text-ink-soft">
        Последние заказы
      </h2>

      <ul className="flex flex-col gap-2">
        {orders.map((order, index) => (
          <RecentRow key={order.id} order={order} showsRepeat={index === 0} />
        ))}
      </ul>
    </section>
  );
}

function RecentRow({
  order,
  showsRepeat,
}: {
  order: ConsumerOrderHistoryRow;
  showsRepeat: boolean;
}) {
  const row = historyRow({
    state: order.state,
    totalPrice: order.total_price,
    fuelAmountLiters: order.fuel_amount_liters,
    chargedOnCancellation: order.charged_on_cancellation,
    cancellationFeeAmount: order.cancellation_fee_amount,
  });

  return (
    <li className="flex items-center gap-3 rounded-md border border-line px-3.5 py-3">
      <Link
        to="/order/$orderId"
        params={{ orderId: order.id }}
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        <CarBrandLogo brand={order.car_brand} size={34} />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-bold leading-[1.3]">{row.title}</span>
          <span className="block truncate text-[12.5px] leading-[1.28] text-ink-soft">
            {historyCarLabel(order.car_brand, order.car_model, order.car_count)} ·{' '}
            {historyDateTimeLabel(order.created_at)}
          </span>
        </span>
      </Link>

      {showsRepeat && <RepeatOrderLink orderId={order.id} />}
    </li>
  );
}
